import React, {useEffect, useState, useMemo, useCallback} from 'react';
import { compose } from 'redux';
import cloneDeep from 'lodash/cloneDeep';
import MediaQuery from 'react-responsive';
import { CSSTransition } from 'react-transition-group';

import './styles.css';
import FiltersContentLoader from '../../components/FiltersContentLoader';
import GridHistoryWithFilters from '../../components/GridHistoryWithFilters';
import HorizontalLoader from '../../components/HorizontalLoader';
import ListSearch from '../../components/ListSearch';
import ListSearchContentLoader from '../../components/ListSearchContentLoader';
import NoEventsMask from '../../components/NoEventsMask';
import RatedByListFilter from '../../components/RatedByListFilter';
import {ANIMATION_TIMEOUT_APPEAR} from '../../constants/animation';
import ENDPOINT from '../../constants/endpoints';
import {MESSAGE_TYPE} from '../../constants/message-types';
import {updateHistory} from '../../helpers/grid-history';
import {Permission, Role} from '../../helpers/user';
import withSearchActions from '../../hocs/withSearchActions';
import withTakeActions from '../../hocs/withTakeActions';
import useApi from '../../hooks/useApi';
import {useGrid} from '../../hooks/useGrid';
import {useGridFilters} from '../../hooks/useGridFilters';
import {useSelection} from '../../hooks/useSelection';
import PageLayout from '../../layouts/PageLayout';
import FiltersAccordion from '../../scenes/FiltersAccordion';
import FiltersWidget from '../../scenes/FiltersAccordion/components/FiltersWidget';
import List from '../../scenes/List';
import CandidateItem from '../../scenes/List/components/CandidateItem';
import Sidebar from '../../scenes/Sidebar';
import FiltersMobileToggler from '../../scenes/Sidebar/components/FiltersMobileToggler';
import WidgetOnMobile from '../../scenes/Sidebar/components/WidgetOnMobile';

const RatedCandidates = props => {
  const {
    user,
    history,
    searchAction: {
      addToShortlist,
      messageSelected,
      message,
      massSendJob,
      downloadResumeRated,
      downloadExcelRated,
    },
    takeAction,
  } = props;

  const api = useApi();
  const [ratedby, setRatedby] = useState();

  const filters = useGridFilters(ENDPOINT.FILTERS.RATED);

  const grid = useGrid(ENDPOINT.COLLECTION.RATED, params => {
    updateHistory(history, params);
  });

  const [
    { isSelected, selectAll, deselectAll, select, deselect, getSelectedIds },
  ] = useSelection(grid.data.ids);

  const [mobileFilters, toggleMobileFilters] = useState(false);

  const toggleMobileFiltersHandler = () => {
    toggleMobileFilters(!mobileFilters);
  };

  const [
    { ratedBy: ratedBySelectOptions, ratedOn: ratedOnSelectOptions },
    setSelectOptions,
  ] = useState({ ratedBy: [], ratedOn: [] });

  useEffect(() => {
    api.get(
      ENDPOINT.OTHER.RATED_SELECT_OPTIONS,
      {},
      { handleSuccess: data => setSelectOptions(data) }
    );
  }, []);

  const [
    isAllowedMassSendJob,
    isRecruiterPremiumRecFirm,
    isAllowedCandidatesShortlists
  ] = useMemo(
    () => [
      Permission.isAllowedMassSendJob(user.permissions),
      Role.isRecruiterPremiumRecFirm(user),
      Permission.isAllowedCandidatesShortlists(user.permissions),
    ],
    [user]
  );

  const filtersAccordion = useMemo(
    () =>
      filters.data && (
        <FiltersAccordion
          title={'Filters'}
          subTitle={'Use the search Filters to narrow your candidate search.'}
          filters={filters.data}
          onChange={filters.actions.change}
          onGroupSelectAll={filters.actions.groupSelect}
          onGroupClearAll={filters.actions.groupClear}
          onClearAll={filters.actions.clear}
          onSearch={filters.actions.search}
          onPushValue={filters.actions.push}
          render={renderProps => <FiltersWidget {...renderProps} />}
        />
      ),
    [filters.data]
  );

  const handleOnSelectAll = checked => {
    checked ? selectAll() : deselectAll();
  };

  const handleDownloadResume = item => {
    takeAction.resume(item);
  };

  const sendMessage = ({ CandidateID, FullName }) => {
    message(CandidateID, FullName, MESSAGE_TYPE.TYPE_RECRUITER_SEARCH_OTHER);
  };

  const handleRatedCandidate = item => {
    takeAction
      .rate(item)
      .then(grid.actions.fetch)
      .catch(() => {});
  };

  const sendMassSetupEmail = () => {
    massSendJob(getSelectedIds());
  };

  const sendMassMessage = () => {
    messageSelected(getSelectedIds(), MESSAGE_TYPE.TYPE_RECRUITER_SEARCH_OTHER);
  };

  const handleMassDownloadResume = () => {
    downloadResumeRated(getSelectedIds(), filters.actions.getQuery());
  };

  const handleDownloadExcel = useCallback(() => {
    downloadExcelRated(getSelectedIds(), filters.actions.getQuery());
  }, [downloadExcelRated, filters]);

  const addShortlistHandler = () => {
    addToShortlist(getSelectedIds(), isRecruiterPremiumRecFirm);
  };

  const handleChangeRatedBy = (field, value) => {
    const ratedby = value ? value.map(val => val.id) : null;
    setRatedby(ratedby);
    const params = cloneDeep({ ...grid.data.params, ratedby });
    grid.actions.fetch(params);
  };

  const handleChangeRatedOn = ({ value }) => {
    const params = cloneDeep({ ...grid.data.params, ratedon: value || null });
    grid.actions.fetch(params);
  };

  const searchActionButtons = [
    {
      caption: 'Send Job',
      controlFunc: sendMassSetupEmail,
      hidden: !isAllowedMassSendJob,
    },
    {
      caption: 'Message',
      controlFunc: sendMassMessage,
    },
    {
      caption: 'Resumes',
      hidden: Role.isRecruiterRestricted(user),
      controlFunc: handleMassDownloadResume,
    },
    {
      caption: 'Excel',
      controlFunc: handleDownloadExcel,
    },
    {
      caption: 'Shortlist',
      controlFunc: addShortlistHandler,
      hidden: !isAllowedCandidatesShortlists,
    },
  ];

  const searchActionSelectors = [
    {
      caption: 'Select All',
      icon: 'tick',
      iconActive: 'cross',
      controlFunc: handleOnSelectAll,
    },
  ];

  const itemFooterActions = [
    {
      caption: 'Download Resume',
      icon: 'download',
      handler: handleDownloadResume,
    },
    {
      caption: 'Message',
      icon: 'message',
      handler: sendMessage,
    },

    {
      caption: 'Rate',
      icon: 'rate',
      handler: handleRatedCandidate,
    },
    {
      caption: 'Tag',
      icon: 'tag',
      hidden: !Role.isRecruiterPremiumRecFirm(user),
      handler: takeAction.tag,
    },
  ];

  const sortControls = [
    {
      defaultCaption: 'Last Login',
      toggledCaption: 'Last Login',
      value: 'default',
    },
    {
      defaultCaption: 'Rating',
      toggledCaption: 'Rating',
      value: 'rating',
    },
    {
      defaultCaption: 'GAT Score',
      toggledCaption: 'GAT Score',
      value: 'gat',
    },
  ];

  const ratedBySelectedList = useMemo(() => {
    return ratedby
      ? ratedBySelectOptions.filter(item => ratedby.indexOf(item.id) !== -1)
      : [];
  }, [ratedby]);

  return (
    <GridHistoryWithFilters
      history={history}
      grid={grid}
      filters={filters}
      initialParams={{
        sortBy: 'default',
        loadPrivateCount: 'Y',
      }}
    >
      <PageLayout
        pageClass="search-page"
        pageTitle="Rated Candidates"
        mainContent={
          <>
            {grid.data.init ? (
              <ListSearch
                sortBy={grid.data.params.sortBy}
                sortControls={sortControls}
                recordsPrefixName="Public"
                recordsCount={grid.data.rowsCount}
                recordsName={
                  Number(grid.data.rowsCount) === 1 ? 'match' : 'matches'
                }
                statsCount={grid.data.countPrivate}
                statsName={
                  Number(grid.data.countPrivate) === 1 ? 'match' : 'matches'
                }
                statsPrefixName="Private"
                search={grid.data.params.search}
                searchPlaceholder="Type your search here"
                onChangeSortByHandler={grid.actions.sortBy}
                onSendSearchQueryHandler={grid.actions.search}
                actionButtonControls={searchActionButtons}
                actionSelectControls={searchActionSelectors}
                renderHeader={
                  <MediaQuery query="(max-width: 767px)">
                    <FiltersMobileToggler
                      title="Search Filters:"
                      toggleFunc={toggleMobileFiltersHandler}
                    />
                  </MediaQuery>
                }
              >
                <RatedByListFilter
                  ratedBySelectedList={ratedBySelectedList}
                  ratedByList={ratedBySelectOptions}
                  ratedOnList={ratedOnSelectOptions}
                  ratedByOnChangeHandler={handleChangeRatedBy}
                  ratedOnOnChangeHandler={handleChangeRatedOn}
                />
              </ListSearch>
            ) : (
              <ListSearchContentLoader />
            )}

            <div className="horizontal-loader__container">
              {grid.data.isFetching && <HorizontalLoader />}
            </div>

            <List
              items={grid.data.rows}
              itemRender={(item, actions) => (
                <CandidateItem
                  key={item.CandidateID}
                  user={user}
                  item={item}
                  actions={actions}
                  selected={isSelected(item.CandidateID)}
                  footerActions={itemFooterActions}
                />
              )}
              selectable
              actions={{
                onSelect: select,
                onDeselect: deselect,
                onChangePage: grid.actions.readPage,
              }}
              pagination={{
                page: grid.data.page,
                count: grid.data.rowsCount,
                perPage: grid.data.perPage,
              }}
            />
          </>
        }
        sidebarContent={
          grid.data.init ? (
            <Sidebar>
              {grid.data.isFetching && (
                <CSSTransition
                  in={grid.data.init}
                  appear={true}
                  timeout={ANIMATION_TIMEOUT_APPEAR}
                  classNames="fade"
                >
                  <NoEventsMask relative={true} />
                </CSSTransition>
              )}
              <WidgetOnMobile
                renderComponent={filtersAccordion}
                status={mobileFilters}
                toggleFunc={toggleMobileFiltersHandler}
              />
            </Sidebar>
          ) : (
            <FiltersContentLoader />
          )
        }
      />
    </GridHistoryWithFilters>
  );
};

export default compose(withSearchActions, withTakeActions)(RatedCandidates);
